import { ComponentProps, Fragment, memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Moon, Sun } from 'lucide-react';

import { deepGet } from '@/utils';

import { ROUTE_WORKFLOW_DASHBOARD } from '@/constant/routes';

import { NavbarSection } from '@/components/layouts/header/navbarSection/navbarSection';
import { NavUser } from '@/components/layouts/header/user.navbar';
import { LogoStratumn } from '@/components/ui/customIcons/logoStratumn';
import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import { env } from '@/constant/env';
import { useUser } from '@/hooks/useUser';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator
} from '@/shadcn/breadcrumb';
import { Button } from '@/shadcn/button';
import { cn } from '@/shadcn/lib/utils';
import { useTheme } from '@/shadcn/theme/theme-provider';
import { BottomLink } from './bottomLink/bottomLink.header';

interface BottomLevel {
  workflowPage?: boolean;
  infoContext?: {
    links?: {
      icon?: string;
      label?: string;
      path?: string;
    }[];
  };
  actions?: {
    links: ComponentProps<typeof BottomLink>[];
  };
}

export type NavbarConfig = {
  loading?: boolean;
  bottomLevel?: BottomLevel;
};

type Props = {
  config?: NavbarConfig;
};

export const Navbar = memo(({ config }: Props) => {
  const loading = config?.loading ?? false;
  const bottomLevel = config?.bottomLevel ?? null;

  const currentEnv = env.VITE_ENVIRONMENT;

  const { user } = useUser();
  const { setTheme, theme } = useTheme();

  const onChangeTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const renderSubNavbarLeftSide = useMemo(() => {
    if (!deepGet(bottomLevel, 'infoContext.links')) return null;
    if (!bottomLevel?.infoContext?.links) return null;
    if (loading) return null;

    const items = [
      {
        icon: 'lucide_House',
        label: 'Home',
        path: ROUTE_WORKFLOW_DASHBOARD
      },
      ...bottomLevel.infoContext.links
    ];

    return (
      <Breadcrumb>
        <BreadcrumbList className="text-[13px]">
          {items.map((item, index) => (
            <Fragment key={index}>
              <BreadcrumbItem key={item.label}>
                <BreadcrumbLink href={item.path} asChild>
                  {item.path ? (
                    <Link
                      to={item.path}
                      className="flex h-full items-center gap-2"
                    >
                      {item.icon && <DynamicIcon icon={item.icon} size={16} />}
                      <span className="pt-0.5">{item.label}</span>
                    </Link>
                  ) : (
                    <div className="hover:text-muted-foreground flex h-full cursor-default items-center gap-2">
                      {item.icon && <DynamicIcon icon={item.icon} size={16} />}
                      <span className="pt-0.5">{item.label}</span>
                    </div>
                  )}
                </BreadcrumbLink>
              </BreadcrumbItem>
              {index !== items.length - 1 && <BreadcrumbSeparator />}
            </Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    );
  }, [!loading && deepGet(bottomLevel, 'infoContext.links')]);

  const renderSubNavbarRightSide = useMemo(() => {
    if (!bottomLevel || !bottomLevel.actions) return undefined;

    const { links } = bottomLevel.actions;

    return (
      <div className="text-muted-foreground flex flex-row items-center text-xs">
        {links?.map((link, index) => (
          <Fragment key={index}>
            <BottomLink {...link} />
          </Fragment>
        ))}
      </div>
    );
  }, [bottomLevel && bottomLevel.actions]);

  const hasSubNavbar = !!renderSubNavbarLeftSide || !!renderSubNavbarRightSide;

  return (
    <header
      className="h-navbar sentry-unmask @container/navbar w-full"
      data-cy="atomic-navbar"
    >
      <div className="bg-card text-card-foreground fixed top-0 right-0 left-0 z-4 w-full">
        <div
          className="h-navbar border-border relative flex w-full items-center border-b pr-5 pl-2.5"
          data-has-subheader={hasSubNavbar}
        >
          <Link
            to={ROUTE_WORKFLOW_DASHBOARD}
            data-cy="go-home"
            aria-label="Go to the home page"
            className="text-primary-deep dark:text-card-foreground p-1.5 pr-[17px] pb-3 pl-3.5 hover:brightness-80"
          >
            <LogoStratumn axis="y" size={24} />
          </Link>
          <div className="flex grow justify-between">
            <NavbarSection className="order-1 max-w-7/10 gap-2 overflow-hidden @max-[470px]/navbar:hidden">
              <Button variant="link">
                <Link
                  className={cn(
                    'text-primary-deep hover:text-primary-deep/80 flex h-full shrink-0 cursor-pointer items-center justify-center text-base font-semibold tracking-wider underline decoration-2 underline-offset-4 select-none'
                  )}
                  data-cy="atomic-navbar-link"
                  to={ROUTE_WORKFLOW_DASHBOARD}
                  draggable="false"
                >
                  Trace
                </Link>
              </Button>
              <Button variant="link">
                <a
                  className={cn(
                    'text-card-foreground hover:text-primary-deep flex h-full shrink-0 cursor-pointer items-center justify-center text-base font-medium tracking-wider no-underline decoration-2 underline-offset-4 select-none hover:underline'
                  )}
                  data-cy="atomic-navbar-link"
                  href={env.VITE_ANALYTICS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  draggable="false"
                >
                  Analytics
                </a>
              </Button>
              {!currentEnv.includes('release') && (
                <span className="bg-primary-deep/80 text-primary-foreground ml-3 rounded-full px-3 text-sm font-semibold select-none @max-[610px]/navbar:hidden">
                  {currentEnv}
                </span>
              )}
            </NavbarSection>
            <NavbarSection
              className="order-3 ml-auto justify-end gap-4 only:w-full"
              loading={loading}
            >
              {/* <FeedbackButton /> */}
              {user?.isSuperuser && (
                <Button variant="ghost" size="icon" onClick={onChangeTheme}>
                  <Sun className="h-[1.2rem] w-[1.2rem] scale-100 rotate-0 transition-all dark:scale-0 dark:-rotate-90" />
                  <Moon className="absolute h-[1.2rem] w-[1.2rem] scale-0 rotate-90 transition-all dark:scale-100 dark:rotate-0" />
                  <span className="sr-only">Toggle theme</span>
                </Button>
              )}
              <NavUser />
            </NavbarSection>
          </div>
        </div>
        {hasSubNavbar && (
          <div
            className="bg-accent h-subnavbar border-border flex w-full items-center border-b px-5"
            data-cy="atomic-subnavbar"
          >
            <div
              className={cn(
                'flex grow justify-between opacity-100 transition-opacity',
                loading && 'opacity-0'
              )}
            >
              {renderSubNavbarLeftSide && (
                <NavbarSection className="order-1 mr-5 max-w-7/10 overflow-hidden">
                  {renderSubNavbarLeftSide}
                </NavbarSection>
              )}
              {renderSubNavbarRightSide && (
                <NavbarSection className="order-3 justify-end only:w-full">
                  {renderSubNavbarRightSide}
                </NavbarSection>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
});
